import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, onSnapshot, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { shareContent } from '../../utils/clipboard';
import CalendarGrid from './CalendarGrid';
import CalendarSettingsModal from './CalendarSettingsModal';
import './CalendarView.css';

export default function CalendarView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { showToast } = useToast();
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showGuestSettings, setShowGuestSettings] = useState(false);
  const [guestCellSize, setGuestCellSize] = useState(() => localStorage.getItem('calendarCellSize') || 'normal');
  const [joining, setJoining] = useState(false);
  const calendarLoadedOnce = useRef(false);
  const wasMemberRef = useRef(false);
  const isLeavingRef = useRef(false);

  useEffect(() => {
    // Set up real-time listener for calendar updates
    const unsubscribe = onSnapshot(
      doc(db, 'calendars', id),
      (calendarDoc) => {
        if (!calendarDoc.exists()) {
          // Only show "not found" alert if we never loaded the calendar (initial load)
          // If we had calendar data before, it means it was deleted while viewing
          if (!calendarLoadedOnce.current) {
            showToast('التقويم غير موجود', 'error');
          }
          navigate('/dashboard');
          return;
        }

        // Mark that we've successfully loaded the calendar at least once
        calendarLoadedOnce.current = true;

        const calendarData = {
          id: calendarDoc.id,
          ...calendarDoc.data()
        };

        // Check if current user was REMOVED from calendar (was a member, now isn't)
        const isCurrentMember = currentUser && calendarData.members && calendarData.members.includes(currentUser.uid);
        if (currentUser && userRole !== 'superAdmin' && wasMemberRef.current && !isCurrentMember) {
          if (!isLeavingRef.current) {
            // Removed by admin — show toast and navigate
            showToast('تم إزالتك من هذا التقويم', 'info');
            navigate('/dashboard');
          }
          // If voluntarily leaving, handleLeaveCalendar handles navigation after all ops complete
          return;
        }

        // Track last accessed time for current user (only on first load)
        if (isCurrentMember && !wasMemberRef.current) {
          wasMemberRef.current = true;
          const userRef = doc(db, 'users', currentUser.uid);
          const lastAccessedField = `lastAccessed.${id}`;

          updateDoc(userRef, {
            [lastAccessedField]: new Date().toISOString()
          }).catch((error) => {
            console.error('Error updating last accessed:', error);
          });
        }

        // Track membership status
        if (isCurrentMember) {
          wasMemberRef.current = true;
        }

        setCalendar(calendarData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading calendar:', error);
        showToast('حدث خطأ في تحميل التقويم', 'error');
        navigate('/dashboard');
        setLoading(false);
      }
    );

    // Clean up listener on unmount
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, currentUser]);

  function handleBackToDashboard() {
    if (currentUser) {
      navigate('/dashboard');
    } else {
      // Pass redirect path as URL parameter
      const redirectPath = encodeURIComponent(location.pathname);
      navigate(`/login?redirect=${redirectPath}`);
    }
  }

  function handleSettingsUpdated() {
    // Reload calendar data
    async function reloadCalendar() {
      const calendarDoc = await getDoc(doc(db, 'calendars', id));
      if (calendarDoc.exists()) {
        setCalendar({
          id: calendarDoc.id,
          ...calendarDoc.data()
        });
      }
    }
    reloadCalendar();
    setShowSettings(false);
  }

  function handleGuestCellSizeChange(size) {
    setGuestCellSize(size);
    localStorage.setItem('calendarCellSize', size);
    window.dispatchEvent(new CustomEvent('calendarCellSizeChange', { detail: size }));
  }

  async function handleJoinCalendar() {
    if (!currentUser || joining) return;
    try {
      setJoining(true);
      await updateDoc(doc(db, 'calendars', id), { members: arrayUnion(currentUser.uid) });
      await updateDoc(doc(db, 'users', currentUser.uid), { calendars: arrayUnion(id) });
      showToast('تم الانضمام للتقويم بنجاح!', 'success');
    } catch (error) {
      showToast('حدث خطأ أثناء الانضمام. يرجى المحاولة مرة أخرى.', 'error');
    } finally {
      setJoining(false);
    }
  }

  if (loading) {
    return (
      <div className="calendar-view-loading">
        <div className="spinner"></div>
        <p>جاري تحميل التقويم...</p>
      </div>
    );
  }

  if (!calendar) {
    return null;
  }

  const isAdmin = currentUser && calendar.createdBy === currentUser.uid;
  const isSuperAdmin = currentUser && userRole === 'superAdmin';
  const isMember = currentUser && calendar.members && calendar.members.includes(currentUser.uid);

  return (
    <div className="calendar-view-wrapper">
      {/* Header */}
    <header className="calendar-view-header">
      <div className="header-left">
        <button className="back-btn" onClick={handleBackToDashboard}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
          <span>{currentUser ? 'العودة' : 'الرئيسية'}</span>
        </button>
      </div>

      <div className="header-center">
        <img src={isDark ? "/Dlogo512.png" : "/Clogo192.png"} alt="Calendar" className="calendar-header-logo" />
        <h1 className="calendar-title">{calendar.name}</h1>
      </div>

      <div className="header-right">
        {/* Share Button - hidden on mobile for guests */}
        <button className={`icon-btn ${!currentUser ? 'guest-share-btn' : ''}`} onClick={async () => {
          const shareUrl = `${window.location.origin}/calendar/${id}`;
          const shareText = `انضم إلى تقويم "${calendar.name}":`;

          const result = await shareContent({
            title: calendar.name,
            text: shareText,
            url: shareUrl
          });

          if (result.success && result.method === 'copy') {
            showToast('تم نسخ الرابط!', 'success');
          } else if (!result.success && !result.cancelled) {
            showToast('فشلت المشاركة. حاول مرة أخرى.', 'error');
          }
        }} title="مشاركة">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
          </svg>
        </button>

        {/* Settings Button - mobile only, guests only */}
        {!currentUser && (
          <button className="icon-btn guest-settings-btn" onClick={() => setShowGuestSettings(true)} title="الإعدادات">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </button>
        )}

        {/* Settings/Members button - Members and super admins */}
        {currentUser && (isMember || isSuperAdmin) && (
          <button className="icon-btn" onClick={() => setShowSettings(true)} title={(isAdmin || isSuperAdmin) ? "إعدادات" : "الأعضاء"}>
            {(isAdmin || isSuperAdmin) ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            )}
          </button>
        )}

        {/* Login button - Guest only */}
        {!currentUser && (
          <button className="btn-login" onClick={() => {
            // Store redirect path in sessionStorage AND URL param for reliability
            sessionStorage.setItem('redirectAfterLogin', location.pathname);
            const redirectPath = encodeURIComponent(location.pathname);
            navigate(`/login?redirect=${redirectPath}`);
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
            <span>تسجيل الدخول</span>
          </button>
        )}

        {/* Theme toggle */}
        <button className="icon-btn" onClick={toggleTheme}>
          {isDark ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          )}
        </button>
      </div>
    </header>

    {/* Join Calendar Banner - Logged in non-members */}
    {currentUser && !isMember && !isSuperAdmin && (
      <div className="guest-info-banner join-banner">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="8.5" cy="7" r="4"></circle>
          <line x1="20" y1="8" x2="20" y2="14"></line>
          <line x1="23" y1="11" x2="17" y2="11"></line>
        </svg>
        <div className="banner-text">
          <strong>أنت لست عضواً في هذا التقويم. </strong>
          انضم للتقويم لتتمكن من إدارة حجوزاتك ومتابعة التحديثات.
        </div>
        <button className="btn-join-calendar" onClick={handleJoinCalendar} disabled={joining}>
          {joining ? 'جاري الانضمام...' : 'انضم للتقويم'}
        </button>
      </div>
    )}

      {/* Main Content */}
      <main className="calendar-view-main">
        <div className="calendar-container">
          <CalendarGrid calendarId={id} />
        </div>
      </main>
      {/* Guest Cell Size Modal */}
      {showGuestSettings && (
        <div className="modal-overlay" onClick={() => setShowGuestSettings(false)}>
          <div className="guest-settings-modal" onClick={e => e.stopPropagation()}>
            <div className="guest-settings-header">
              <h3 className="guest-settings-title">حجم الخلايا</h3>
              <button className="guest-settings-close" onClick={() => setShowGuestSettings(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="cell-size-toggle">
              <button
                className={`cell-size-btn ${guestCellSize === 'normal' ? 'active' : ''}`}
                onClick={() => handleGuestCellSizeChange('normal')}
              >عادي</button>
              <button
                className={`cell-size-btn ${guestCellSize === 'large' ? 'active' : ''}`}
                onClick={() => handleGuestCellSizeChange('large')}
              >كبير</button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Settings/Members Modal */}
      {showSettings && calendar && (
        <CalendarSettingsModal
          calendar={calendar}
          onClose={() => setShowSettings(false)}
          onUpdated={handleSettingsUpdated}
          isAdmin={isAdmin || isSuperAdmin}
          onLeave={() => { isLeavingRef.current = true; }}
        />
      )}
    </div>
  );
}